import { useEffect, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'
import { fallback, zodValidator } from '@tanstack/zod-adapter'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { getProviderConfig } from '@/lib/constants/connector'
import type { ConnectorProvider } from '@/lib/api/types'

// Phase 24 — OAuth success popup (closes AGENTS.md C7).
// Intentionally NOT under `_authenticated`: the popup may not have Auth0
// in-memory state and we don't want the auth gate to redirect into the
// Auth0 login flow (which would send the popup to `/` via redirect_uri).
// This route only calls window.opener.postMessage + window.close — no
// authenticated API calls — so skipping the gate is safe.

const searchSchema = z.object({
  connectorId: fallback(z.string().optional(), undefined),
})

// API emits segment (google | clickup); Connector.type uses
// google_drive | clickup — invert at this boundary.
const segmentToProvider: Record<string, ConnectorProvider> = {
  google: 'google_drive',
  clickup: 'clickup',
}

export const Route = createFileRoute('/connectors/$provider/success')({
  validateSearch: zodValidator(searchSchema),
  component: SuccessPage,
})

export function SuccessPage() {
  const { provider: segment } = Route.useParams()
  const { connectorId } = Route.useSearch()
  const provider = segmentToProvider[segment] ?? 'google_drive'
  const providerLabel = getProviderConfig(provider).label

  const [showFallbackClose, setShowFallbackClose] = useState(false)

  useEffect(() => {
    document.title = `Connecting ${providerLabel}…`

    if (window.opener && !window.opener.closed) {
      try {
        window.opener.postMessage(
          {
            type: 'oauth-callback',
            provider,
            success: true,
            connectorId,
          },
          window.location.origin,
        )
      } catch {
        /* cross-origin throw — ignore and fall through to auto-close */
      }
    }

    const closeTimer = setTimeout(() => {
      try {
        window.close()
      } catch {
        /* noop */
      }
    }, 250)

    const fallbackTimer = setTimeout(() => {
      setShowFallbackClose(true)
    }, 3000)

    return () => {
      clearTimeout(closeTimer)
      clearTimeout(fallbackTimer)
    }
  }, [provider, connectorId, providerLabel])

  const openerMissing = typeof window !== 'undefined' && !window.opener

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="bg-card rounded-xl shadow-sm border border-border p-8 py-12 flex flex-col items-center gap-3 max-w-md w-full">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
        <h2 className="text-lg font-medium">Connecting…</h2>
        {openerMissing ? (
          <p className="text-sm text-muted-foreground text-center">
            You can close this window and return to Settings.
          </p>
        ) : (
          <p className="text-sm text-muted-foreground text-center">
            Finishing sign-in to {providerLabel}. This window will close automatically.
          </p>
        )}
        {showFallbackClose ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              try {
                window.close()
              } catch {
                /* noop */
              }
            }}
          >
            Close this window
          </Button>
        ) : null}
      </div>
    </div>
  )
}
